import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { ProjectForm } from '../models';
import { CenteredSpinner } from '../../../components';
import {
  useUpdatePhaseMutation,
  useGetPhaseByIdQuery,
  useCreatePhaseMutation,
  displayNotification,
} from '../../../services';
import { NewPhase } from '../components';
import { transformDate, formatDate } from '../../../utils';
import { Phase } from '../../../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

const newPhase = {
  phaseName: '',
  phaseEstimatedEndDate: undefined,
  phaseStartDate: undefined,
};

export const SavedPhase: FC = () => {
  const { phaseId, projectId } = useParams();

  const { data: phase, isLoading } = useGetPhaseByIdQuery(
    {
      projectId: Number(projectId),
      phaseId: Number(phaseId),
    },
    {
      skip: !phaseId,
    }
  );

  const navigate = useNavigate();

  const [createPhase] = useCreatePhaseMutation();
  const [updatePhase] = useUpdatePhaseMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<Pick<ProjectForm, 'phases'>>({
    defaultValues: {
      phases: [newPhase],
    },
  });

  const navigateToPreviousPage = () => {
    if (phaseId)
      navigate(
        `/projects/multiple-phase/${String(
          projectId
        )}/project-details/phases/${phaseId}`
      );
    else
      navigate(
        `/projects/multiple-phase/${String(projectId)}/project-details/phases`
      );
  };

  async function onSubmit(data: Pick<ProjectForm, 'phases'>) {
    let { phases } = data;

    phases = phases.map(phase => ({
      ...phase,
      phaseEstimatedEndDate: formatDate(phase.phaseEstimatedEndDate),
      phaseStartDate: formatDate(phase.phaseStartDate),
    }));

    try {
      // Neither trigger is unwrapped: a rejected save resolved normally and
      // the page navigated back as if the phase had been stored.
      const response = phaseId
        ? await updatePhase({
            projectId: Number(projectId),
            phase: phases[0] as Phase,
          })
        : await createPhase({
            projectId: Number(projectId),
            phases,
          });

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: phaseId
            ? 'Could not update this phase'
            : 'Could not create the phase',
          description:
            'Your changes were not saved. Please review the dates and try again.',
        });
        return;
      }

      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your changes were not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (phase?.data) {
      reset({
        phases: [
          {
            ...phase.data,
            phaseStartDate: transformDate(phase.data.phaseStartDate),
            phaseEstimatedEndDate: transformDate(
              phase.data.phaseEstimatedEndDate
            ),
          },
        ],
      });
    }
  }, [phase?.data, reset]);

  if (isLoading) return <CenteredSpinner />;

  return (
    <NewPhase
      control={control}
      errors={errors}
      isLoading={isSubmitting}
      register={register}
      handleAddPhase={isAdded => {
        if (isAdded) void handleSubmit(onSubmit)();
        else navigateToPreviousPage();
      }}
    />
  );
};
